<script>
import { mapState, mapActions } from "vuex";
import { formatPool } from "@/utils";
import ContractNavigationWrapper from "@/components/ContractNavigationWrapper";
import MultiSelect from "@/components/MultiSelect";

export default {
  // Included components
  components: {
    ContractNavigationWrapper,
    MultiSelect,
  },

  data() {
    return {
      teamId: null,
      facilityId: null,
      batch_number: null,
      rate_per_kWh: null,
      machine_setup_fee: null,
      machine_security_deposit: null,
      machine_insurance: null,
      term_length: null,
      machine_count: null,
      customer_type: null,
      device_type_id: null,
      compatible: null,
      hourly_watt_consumption: null,
      shipped_or_transferred: null,
      contract_template: null,
      client_name: null,
      client_email: null,
      new_or_existing_customer: null,
      team_name: null,
      poolId: null,
      miningPoolUsername: null,
      template_choices: [],
      allTeamsForContracts: [],
      contractFacilities: [],
      serialNumbersListRaw: "",
      client_company: "",
      tracking_number: "",
      shipping_provider: "",
      selectedTransferTeam: null,
      teamWorkers: {},
      start_date: "",
      expiration: "",
      ccEmailOne: "",
      ccEmailTwo: "",

      // Workers selected to be in the Contract
      selectedWorkers: [],
    };
  },

  computed: {
    // Whether the form is being used to edit (vs. create)
    editing() {
      return this.contractId != null;
    },

    // Options for the device selector
    deviceOptions() {
      return this.deviceTypes.map((type) => {
        return { value: type.id, text: type.name };
      });
    },

    // Options for the team selector on Transferred Workers
    teamOptions() {
      return this.allTeamsForContracts.map((type) => {
        return { value: type.id, text: type.handle };
      });
    },
    // Options for the ContractFacility selector
    facilityOptions() {
      return this.contractFacilities.map((type) => {
        return { value: type.id, text: type.name };
      });
    },
    // origin (shipped or transferred) options for selector
    originOptions() {
      return [
        { value: "shipped", text: "Shipped" },
        { value: "transferred", text: "Transferred" },
      ];
    },
    // Customer Type options for selection
    customerTypeOptions() {
      return [
        { value: "Navier", text: "Navier" },
        { value: "Blockware", text: "Blockware" },
      ];
    },
    // Compatibility Options for selector
    compatibilityOptions() {
      return [
        { value: true, text: "Yes" },
        { value: false, text: "No" },
      ];
    },
    // HelloSign Template Options for selector
    templateOptions() {
      return this.template_choices.map((template) => {
        return { value: template.template_id, text: template.title };
      });
    },
    // options for New or Existing selector
    newOrExistingOptions() {
      return [
        { value: true, text: "New" },
        { value: false, text: "Returning" },
      ];
    },

    // Options for the pool selector
    poolOptions() {
      return this.pools.map((pool) => {
        return {
          value: pool.id,
          text: formatPool(pool, true),
        };
      });
    },
    // IDs of selected workers to be marked for transfer on the HelloSign Agreement
    selectedWorkerIds() {
      return this.selectedWorkers.map((worker) => worker.id);
    },

    // Mapped vuex state
    ...mapState(["contracts", "deviceTypes", "pools", "teams", "user"]),
  },

  watch: {
    selectedTransferTeam: function () {
      this.getTeamWorkers();
    },
  },

  // When the component is created, fetch the teams for contracts, contract facilities, and hellosign templates
  async created() {
    if (!this.user.isStaffAdmin) {
      await this.$router.push("/");
    }

    this.allTeamsForContracts = await this.getAllTeamsForContracts();
    this.contractFacilities = await this.getContractFacilities();
    this.template_choices = await this.getHelloSignTemplates();
    await Promise.all([
      this.getDeviceTypes(),
      this.getPools(),
      this.getTeams(),
    ]);
  },

  methods: {
    // On submit, either put or post the form data to the API,
    // depending on whether in edit or create mode
    async submit() {
      this.post();
    },
    async getTeamWorkers() {
      this.teamWorkers = await this.getTransferWorkersForTeam({
        teamId: this.selectedTransferTeam,
      });
      this.selectedWorkers = [];
    },
    // Search prop for multiselect element
    workerMatchesSearch(search, worker) {
      search = search.trim().toLowerCase();
      return worker.name.toLowerCase().includes(search);
    },

    // Post the contract details to HelloSign endpoint
    async post() {
      // Update given the form inputs
      const success = await this.onboardClient({
        template_id: this.contract_template,
        signer_name: this.client_name,
        signer_email: this.client_email,
        team_id: this.teamId,
        team_name: this.team_name,
        batch_number: this.batch_number,
        model: this.device_type_id,
        kwh_rate: this.rate_per_kWh,
        term: this.term_length,
        quantity: this.machine_count,
        setup_fee_per_miner: this.machine_setup_fee,
        security_deposit_per_miner: this.machine_security_deposit,
        insurance_per_miner: this.machine_insurance,
        wattage: this.hourly_watt_consumption,
        serials: this.serialNumbersListRaw,
        shipping_provider: this.shipping_provider,
        tracking_number: this.tracking_number,
        workers: this.selectedWorkers,
        start_date: this.start_date,
        expiration: this.expiration,
        ccEmailOne: this.ccEmailOne,
        ccEmailTwo: this.ccEmailTwo,
        metadata: {
          new_customer: this.new_or_existing_customer,
          customer_type: this.customer_type,
          compatibility: this.compatible,
          shipped_or_transferred: this.shipped_or_transferred,
          facility: this.facilityId,
          pool: this.poolId,
          pool_acct: this.miningPoolUsername,
        },
      });
      // // If success, then redirect
      if (success) {
        await this.$router.push("/contracts");
      }
    },

    // Mapped vuex actions
    ...mapActions([
      "getDeviceTypes",
      "onboardClient",
      "getPools",
      "getTeams",
      "getAllTeamsForContracts",
      "getContractFacilities",
      "getPools",
      "getHelloSignTemplates",
      "getTransferWorkersForTeam",
    ]),
  },
};
</script>

<template>
  <ContractNavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/contracts">Contracts</b-breadcrumb-item>
      <b-breadcrumb-item active>Onboard A New Client</b-breadcrumb-item>
    </template>
    <h1 class="mb-3">Onboard A New Client</h1>
    <div>
      <b-row>
        <b-col>
          <b-card class="mb-3">
            <b-form @submit.stop.prevent="submit">
              <b-row>
                <b-col>
                  <b-form-group
                    id="facility"
                    label="Facility"
                    label-for="facility"
                  >
                    <b-form-select
                      required
                      v-model="facilityId"
                      id="facilityId"
                      :options="facilityOptions"
                    >
                      <template #first>
                        <option :value="null" disabled></option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      Select a facility for the customer
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="contract-template"
                    label="Contract Template"
                    label-for="contractTemplate"
                  >
                    <b-form-select
                      required
                      v-model="contract_template"
                      id="contractTemplate"
                      :options="templateOptions"
                    >
                      <template #first>
                        <option :value="null" disabled></option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      Select a Hellosign Contract Template
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>

              <hr />
              <h4>Input Contract Terms</h4>
              <br />
              <b-row>
                <b-col>
                  <b-form-group
                    id="client-name"
                    label="Client Name"
                    label-for="Client Name"
                  >
                    <b-form-input
                      required
                      id="clientName"
                      v-model="client_name"
                      placeholder="Name"
                      autocomplete="off"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="client-email"
                    label="Client Email"
                    label-for="clientEmail"
                  >
                    <b-form-input
                      required
                      id="clientEmail"
                      v-model="client_email"
                      placeholder="Email"
                      autocomplete="off"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="client-company"
                    label="Company (Optional)"
                    label-for="client company"
                  >
                    <b-form-input
                      id="clientCompany"
                      v-model="client_company"
                      placeholder="Company"
                      autocomplete="off"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <hr />
              <h4>Input Operational Terms</h4>
              <div>
                <p>
                  Please select whether this customer is a new or returning
                  customer. If new, please select "New Customer" from the
                  dropdown and input the team name to be created. If returning,
                  please select "Existing Customer" and input the customers
                  current team.
                </p>
                <br />
              </div>

              <b-row>
                <b-col>
                  <b-form-group
                    id="new-or-existing-customer"
                    label="New or Existing Customer"
                    label-for="newOrExistingCustomer"
                  >
                    <b-form-select
                      required
                      v-model="new_or_existing_customer"
                      id="newOrExistingCustomer"
                      :options="newOrExistingOptions"
                    >
                      <template #first>
                        <option :value="null" disabled></option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      Is this customer new or returning?
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="customer-type"
                    label="Customer Type"
                    label-for="customerType"
                  >
                    <b-form-select
                      required
                      v-model="customer_type"
                      id="customerType"
                      :options="customerTypeOptions"
                    >
                      <template #first>
                        <option :value="null" disabled>
                          Navier or Blockware
                        </option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      Is the customer from Navier or Blockware?
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-form-group
                    id="cc-email-one"
                    label="CC Email #1"
                    label-for="cc-email-one"
                  >
                    <b-form-input
                      id="cc-email-one"
                      v-model="ccEmailOne"
                      placeholder="email@domain.com"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Enter two emails that you would like copied on this
                      HelloSign Contract
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="cc-email-two"
                    label="CC Email #2"
                    label-for="cc-email-two"
                  >
                    <b-form-input
                      id="cc-email-two"
                      v-model="ccEmailTwo"
                      placeholder="email@domain.com"
                      autocomplete="off"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group id="Team" label="Team Name" label-for="TeamName">
                <b-form-input
                  required
                  id="TeamName"
                  v-model="team_name"
                  placeholder="Team Name"
                  autocomplete="off"
                ></b-form-input>
                <small class="form-text text-muted">
                  Enter the exact name of the team. If the customer is
                  <b>new</b>
                  , input the name of the team to be created.
                </small>
              </b-form-group>
              <b-row>
                <b-col>
                  <b-form-group
                    id="destination-worker-pool-group"
                    label="Mining Pool"
                    label-for="destinationWorkerPool"
                  >
                    <b-form-select
                      v-model="poolId"
                      id="destinationWorkerPool"
                      :options="poolOptions"
                    >
                      <template #first>
                        <option :value="null" disabled>Pool</option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      <b>Required only for incompatible contracts</b>
                      , otherwise optional
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="mining-pool-username"
                    label="Pool Username"
                    label-for="miningPoolUsername"
                  >
                    <b-form-input
                      id="miningPoolUsername"
                      v-model="miningPoolUsername"
                      placeholder="Pool Account"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      <b>Required only for incompatible contracts</b>
                      , otherwise optional
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>

              <hr />
              <h4>Input Hosting And Machine Terms</h4>
              <br />

              <b-row>
                <b-col>
                  <b-form-group
                    id="batch-number"
                    label="Batch Number"
                    label-for="batchNumber"
                  >
                    <b-form-input
                      required
                      id="batchNumber"
                      v-model="batch_number"
                      placeholder="0"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">Numbers Only</small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="device-type-group"
                    label="Machine Model"
                    label-for="deviceTypes"
                  >
                    <b-form-select
                      required
                      v-model="device_type_id"
                      id="deviceTypes"
                      :options="deviceOptions"
                    >
                      <template #first>
                        <option :value="null" disabled></option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      Type of machines on this contract
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="compatible"
                    label="Machine Compatibility with Mine.app"
                    label-for="compatible"
                  >
                    <b-form-select
                      required
                      v-model="compatible"
                      id="compatible"
                      :options="compatibilityOptions"
                    >
                      <template #first>
                        <option :value="null" disabled>Yes or No</option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      Note: Transfers from Other Teams must be compatible
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col>
                  <b-form-group
                    id="machine-count"
                    label="Quantity of Machines"
                    label-for="machineCount"
                  >
                    <b-form-input
                      required
                      id="machineCount"
                      v-model="machine_count"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">Numbers only</small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="rate-per-kWh"
                    label="Rate/kWh"
                    label-for="ratePerKWh"
                  >
                    <b-form-input
                      required
                      id="ratePerKWh"
                      v-model="rate_per_kWh"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      In dollars (numbers only)
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="hourly-Watt-consumption"
                    label="Hourly Watt Consumption"
                    label-for="hourlyWattConsumption"
                  >
                    <b-form-input
                      required
                      id="hourlyWattConsumption"
                      v-model="hourly_watt_consumption"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">Numbers only</small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group
                id="serial-number-group"
                label="Serial Numbers (Optional)"
                label-for="serial_number"
              >
                <b-form-textarea
                  id="serial_number"
                  v-model="serialNumbersListRaw"
                  placeholder="Serial Number for Worker 1, Serial Number for Worker 2, ..."
                  class="form-control"
                  rows="4"
                  no-resize
                ></b-form-textarea>
                <small class="form-text text-muted">
                  Worker serial numbers are unique identifier for worker units.
                  Numbers are separated by commas or newlines.
                </small>
              </b-form-group>
              <b-row>
                <b-col>
                  <b-form-group
                    id="term-length"
                    label="Term Length (Months)"
                    label-for="termLength"
                  >
                    <b-form-input
                      required
                      id="termLength"
                      v-model="term_length"
                      placeholder="0"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">Numbers only</small>
                  </b-form-group>
                </b-col>

                <b-col>
                  <b-form-group
                    id="startDateGroup"
                    label="Contract Start"
                    label-for="startDate"
                  >
                    <b-form-datepicker
                      id="startDate"
                      v-model="start_date"
                    ></b-form-datepicker>
                    <small class="form-text text-muted">
                      Contract Start Date
                    </small>
                  </b-form-group>
                </b-col>

                <b-col>
                  <b-form-group
                    id="expirationDateGroup"
                    label="Contract Expiry"
                    label-for="expirationDate"
                  >
                    <b-form-datepicker
                      id="expirationDate"
                      v-model="expiration"
                    ></b-form-datepicker>
                    <small class="form-text text-muted">
                      Contract Expiration Date
                    </small>
                  </b-form-group>
                </b-col>

                <b-col>
                  <b-form-group
                    id="machine-setup-fee"
                    label="Setup Fee"
                    label-for="machineSetupFee"
                  >
                    <b-form-input
                      required
                      id="machineSetupFee"
                      v-model="machine_setup_fee"
                      placeholder="0"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Per Unit; Numbers only
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="machine-security-deposit"
                    label="Security Deposit"
                    label-for="machineSecurityDeposit"
                  >
                    <b-form-input
                      required
                      id="machineSecurityDeposit"
                      v-model="machine_security_deposit"
                      placeholder="0.0"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Per Unit; Numbers only
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="machine-insurance"
                    label="Insurance"
                    label-for="machineInsurance"
                  >
                    <b-form-input
                      required
                      id="machineInsurance"
                      v-model="machine_insurance"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Per Unit; Numbers only
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-form-group
                    id="shipped_or_transferred"
                    label="Miner Origin"
                    label-for="shippedOrTransferred"
                  >
                    <b-form-select
                      required
                      v-model="shipped_or_transferred"
                      id="shippedOrTransferred"
                      :options="originOptions"
                    >
                      <template #first>
                        <option :value="null" disabled>
                          Shipped Or Transferred
                        </option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      Were the machines shipped or transferred
                    </small>
                  </b-form-group>
                  <b-form-group
                    v-if="shipped_or_transferred == 'transferred'"
                    label="Select a Team from which to Transfer Workers"
                  >
                    <b-form-select
                      v-model="selectedTransferTeam"
                      :options="teamOptions"
                    ></b-form-select>
                  </b-form-group>
                  <b-form-group
                    label="Worker Names To Be Transferred"
                    v-if="
                      shipped_or_transferred == 'transferred' &&
                      selectedTransferTeam !== null &&
                      this.teamWorkers
                    "
                  >
                    <MultiSelect
                      v-model="selectedWorkers"
                      :options="teamWorkers"
                      :match="workerMatchesSearch"
                    ></MultiSelect>
                  </b-form-group>
                  <b-form-group
                    label="Shipping Provider and Tracking Number"
                    v-else-if="shipped_or_transferred == 'shipped'"
                  >
                    <b-form-input
                      placeholder="Shipping Service Provider"
                      v-model="shipping_provider"
                      id="shipping_provider"
                    >
                      Shipping Provider
                    </b-form-input>
                    <div class="spacer" />
                    <b-form-input
                      placeholder="Tracking No."
                      v-model="tracking_number"
                      id="tracking_number"
                    >
                      Tracking No.
                    </b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-button type="submit" variant="primary">
                Onboard New Client
              </b-button>
            </b-form>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </ContractNavigationWrapper>
</template>

<style>
#toggle-btn {
  padding: 7px 12px;
  margin-top: -1px;
}
.spacer {
  height: 10px;
}
</style>
